import { colors, LineChartOption } from '@creditsights/styleguide-web';
import { inRange, range } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { CqsMetric, HistoricalRecord, RiskProductsData } from '../../../../models/RiskProducts';
import { fontConfig, getTimeSeriesBaseConfig, getTooltipMarkerHtml } from '../../../../utils/chartUtils';
import { DATE_FORMATS, formatCommonDate, formatDate } from '../../../../utils/dateUtils';
import {
  filterRiskMetricsByYearRange,
  mapRiskMetricsToYearsAxisValues,
  sortMetricsDescByCalculationDate,
} from '../RiskProductsWidget.logic';
import { ACR_GROUPED_TO_CQS_MAP, ACR_TO_CQS_MAP } from './CqsChart.logic.constants';
import { YAxisRanges, YAxisValues } from './CqsChart.types';
import { TimeFrameOptions } from '../RiskProductsWidget.types';

const Y_AXIS_INCREMENT_VALUE_BY = 10;
const Y_AXIS_INCREMENT_VALUE_BY_MIN = 5;

export const hasCqsMetrics = (cqsMetrics: CqsMetric[]): boolean => {
  const cqsValues = cqsMetrics.map((metric) => metric.cqs).filter((cqs) => cqs !== null);
  const acrValues = cqsMetrics.map((metric) => metric.acr).filter((acr) => acr !== null);
  return !isEmpty(cqsValues) || !isEmpty(acrValues);
};

// ------------ Filter risk metrics ------------
export const getCqsMetrics = (riskMetrics: RiskProductsData): CqsMetric[] => {
  const cqsMetrics = riskMetrics?.data.length > 0 ? processCqsMetrics(riskMetrics.data) : [];
  return sortMetricsDescByCalculationDate(cqsMetrics);
};

export const processCqsMetrics = (riskMetrics: HistoricalRecord[]): CqsMetric[] => {
  const filteredRiskMetrics = filterRiskMetricsByYearRange(riskMetrics);
  return removeUnexpectedRiskMetrics(filteredRiskMetrics);
};

const isCqsValid = (cqs: number): boolean => {
  return inRange(cqs, 0, 101);
};

const mapAcrValue = (acr: string): number => {
  return ACR_GROUPED_TO_CQS_MAP[acr];
};

const mapAcrUngroupedValue = (acrUngrouped: string): number => {
  return ACR_TO_CQS_MAP[acrUngrouped];
};

const removeUnexpectedRiskMetrics = (allRiskMetrics: HistoricalRecord[]): CqsMetric[] => {
  const expectedRiskMetrics: CqsMetric[] = [];
  allRiskMetrics.forEach((metric) => {
    const cqsValid = metric.cqs !== null && isCqsValid(metric.cqs);
    const acrValid = metric.acr_level_4 && mapAcrValue(metric.acr_level_4) > 0;

    if (cqsValid || acrValid) {
      expectedRiskMetrics.push({
        cqs: cqsValid ? metric.cqs : null,
        acr: acrValid ? metric.acr_level_4 : null,
        calculationDate: metric.calculation_date,
        acrUngrouped: metric.acr ?? undefined,
      });
    }
  });
  return expectedRiskMetrics;
};

// ------------ Risk Metrics Axis Block ------------
const mapRiskMetricsToYAxisValues = (riskMetrics: CqsMetric[]): YAxisValues => {
  const cqsAxisValues: number[] = [];
  const acrAxisValues: number[] = [];
  for (let i = riskMetrics.length - 1; i >= 0; i -= 1) {
    const riskMetric = riskMetrics[i];
    const cqsTickValue: number = riskMetric.cqs !== null ? riskMetric.cqs : -1;
    const acrAxisValue: number = riskMetric.acrUngrouped ? mapAcrUngroupedValue(riskMetric.acrUngrouped) : -1;

    if (cqsTickValue >= 0) {
      cqsAxisValues.unshift(cqsTickValue);
    } else if (cqsAxisValues.length) {
      cqsAxisValues.unshift(cqsTickValue >= 0 ? cqsTickValue : cqsAxisValues[0]);
    }

    if (acrAxisValue >= 0) {
      acrAxisValues.unshift(acrAxisValue);
    } else if (acrAxisValues.length) {
      acrAxisValues.unshift(acrAxisValue >= 0 ? acrAxisValue : acrAxisValues[0]);
    }
  }
  return { cqs: cqsAxisValues, acr: acrAxisValues };
};

const getYAxisRange = (yAxisValues: YAxisValues): number[] => {
  const combinedYAxisValues = [...yAxisValues.cqs, ...yAxisValues.acr];
  const yAxisRange = getYAxisTicks(combinedYAxisValues);
  const cqsTickVals = computeCqsTickVals(yAxisRange);
  const formattedCqsTickVals = addStartEndRanges(cqsTickVals);

  return [formattedCqsTickVals[0], formattedCqsTickVals[formattedCqsTickVals.length - 1]];
};

const getYAxisTicks = (yAxisValues: number[]) => {
  if (!yAxisValues.length) {
    return [];
  }

  const lowestValue = Math.min(...yAxisValues);
  const highestValue = Math.max(...yAxisValues);

  return lowestValue === highestValue ? [highestValue] : range(lowestValue, highestValue + 1);
};

const addStartRange = (yAxisTickValues: number[]): number[] => {
  const minValue = Math.min(...yAxisTickValues);
  if (minValue > 0) {
    yAxisTickValues.unshift(Math.min(...yAxisTickValues) - Y_AXIS_INCREMENT_VALUE_BY);
  }
  return yAxisTickValues;
};

const addEndRange = (yAxisTickValues: number[]): number[] => {
  const maxValue = Math.max(...yAxisTickValues);
  if (maxValue < 100) {
    yAxisTickValues.push(Math.max(...yAxisTickValues) + Y_AXIS_INCREMENT_VALUE_BY);
  }
  return yAxisTickValues;
};

const addStartEndRanges = (yAxisTickValues: number[]): number[] => {
  addStartRange(yAxisTickValues);
  addEndRange(yAxisTickValues);
  return yAxisTickValues;
};

const computeCqsTickVals = (yAxisTickValues: number[]): number[] =>
  yAxisTickValues.filter((value) => value % Y_AXIS_INCREMENT_VALUE_BY === 0);

// change ACR_GROUPED_TO_CQS_MAP to ACR_TO_CQS_MAP if you want the left side to display all acr values
const CQS_TO_ACR_MAP = Object.entries(ACR_GROUPED_TO_CQS_MAP).reduce(
  (acc, value) => ({
    ...acc,
    [value[1]]: value[0],
  }),
  {}
);

export const mapAcrValueToLabel = (interval: number): string => {
  return CQS_TO_ACR_MAP[interval] || '';
};

const getTooltipHtml = (params, cqsMetrics): string => {
  const cqsParam = params.find((val) => val.seriesName === 'CQS');
  const cqsValue =
    cqsParam && cqsParam.value !== null
      ? `  <div>
             ${getTooltipMarkerHtml(cqsParam.color)} ${cqsParam.seriesName} <span style="color: #b5bec5">${
          cqsParam.value
        }</span>
           </div>`
      : '';
  const acrParam = params.find((val) => val.seriesName === 'ACR');
  const acrValue =
    acrParam && cqsMetrics[acrParam.dataIndex].acrUngrouped !== null
      ? `   <div>
             ${getTooltipMarkerHtml(acrParam.color)} ${acrParam.seriesName} <span style="color: #b5bec5">${
          cqsMetrics[acrParam.dataIndex].acrUngrouped
        }</span>
           </div>`
      : '';
  return `
    <div>
      <span style="border-bottom: 1px solid #b5bec5; display: block; padding-bottom: 2px; margin-bottom: 2px;">
        ${formatCommonDate(cqsMetrics[params[0].dataIndex].calculationDate)}
      </span>
      ${cqsValue}
      ${acrValue}
      <span style="position: absolute; bottom: 0; left: 50%; width: 0; height: 0; margin: 0 0 -4px -4px; border-style: solid; border-width: 4px 4px 0 4px; border-color: #141d24 transparent transparent transparent;"></span>
    </div>
`;
};

const getCqsChartYAxisRanges = (yAxisValues: YAxisValues): YAxisRanges => {
  const yAxisRange: number[] = getYAxisRange(yAxisValues);
  let interval: number = Y_AXIS_INCREMENT_VALUE_BY;
  let minYAxisValue: number = yAxisRange[0];
  let maxYAxisValue: number = yAxisRange[1];
  if (yAxisValues.cqs[yAxisValues.cqs.length - 1] - yAxisValues.cqs[0] <= 10) {
    interval = Y_AXIS_INCREMENT_VALUE_BY_MIN;

    const values = addStartEndRanges([
      Math.trunc(Math.min(...yAxisValues.cqs, ...yAxisValues.acr) / 10) * 10,
      Math.ceil(Math.max(...yAxisValues.cqs, ...yAxisValues.acr) / 10) * 10,
    ]);

    // eslint-disable-next-line prefer-destructuring
    minYAxisValue = values[0];
    maxYAxisValue = values[values.length - 1];
  }

  return {
    min: minYAxisValue,
    max: maxYAxisValue,
    interval,
  };
};

export const getCqsChartConfig = (cqsMetrics: CqsMetric[], timeframe?: TimeFrameOptions): LineChartOption => {
  const yAxisValues: YAxisValues = mapRiskMetricsToYAxisValues(cqsMetrics);
  const xAxisValues: number[] = mapRiskMetricsToYearsAxisValues(cqsMetrics);
  const { interval: yAxisInterval, min: yAxisMin, max: yAxisMax }: YAxisRanges = getCqsChartYAxisRanges(yAxisValues);
  const baseChartOptionConfig: LineChartOption = getTimeSeriesBaseConfig(xAxisValues.length, timeframe);

  return {
    ...baseChartOptionConfig,
    legend: {
      ...baseChartOptionConfig.legend,
      data: ['CQS', 'ACR'],
    },
    tooltip: {
      ...baseChartOptionConfig.tooltip,
      formatter: (params) => getTooltipHtml(params, cqsMetrics),
    },
    xAxis: [
      {
        ...baseChartOptionConfig.xAxis?.[0],
        data: xAxisValues,
        axisLabel: {
          ...baseChartOptionConfig.xAxis?.[0].axisLabel,
          formatter: (value, index) => {
            return formatDate(cqsMetrics[index].calculationDate, DATE_FORMATS.MONTH_YEAR_MLA);
          },
        },
        inverse: true,
      },
    ],
    yAxis: [
      {
        type: 'value',
        min: yAxisMin,
        max: yAxisMax,
        interval: yAxisInterval,
        axisLabel: {
          ...fontConfig,
          color: colors.tertiary1000,
          fontStyle: 'normal',
        },
        splitLine: {
          lineStyle: {
            color: colors.tertiary300,
          },
        },
      },
      {
        type: 'value',
        min: yAxisMin,
        max: yAxisMax,
        interval: yAxisInterval,
        axisLabel: {
          ...fontConfig,
          color: colors.tertiary700,
          fontStyle: 'normal',
          formatter: (value) => {
            return mapAcrValueToLabel(value);
          },
        },
        splitLine: {
          lineStyle: {
            color: colors.tertiary300,
          },
        },
      },
    ],
    series: [
      {
        name: 'CQS',
        type: 'line',
        step: 'start',
        showSymbol: false,
        data: yAxisValues.cqs,
        color: '#6D5989',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          join: 'miter',
        },
        silent: true,
      },
      {
        name: 'ACR',
        type: 'line',
        step: 'start',
        showSymbol: false,
        data: yAxisValues.acr,
        color: colors.success600,
        smooth: true,
        symbol: 'none',
        lineStyle: {
          join: 'miter',
        },
        silent: true,
      },
    ],
  };
};
